/**
 * RBAC v2 Permissions Utility
 *
 * Access is defined by permissions (not staffRoleId / role-name hardcodes).
 * Wildcard semantics match API RBACService::checkPermissionInList:
 * - platform.* / platform.operator → everything
 * - admin.* → all non-platform permissions
 *
 * Module ↔ permission mapping lives in accessCatalog.js.
 */
import {
  HOME_MODULE_ORDER,
  getAccessModule,
  getAccessModules,
  getModuleAnyOf,
  permissionAliasFamily,
} from './accessCatalog';

export { MODULE_PERMISSIONS } from './accessCatalog';

function collectPermissionNames(profile) {
  const names = [];
  const add = (value) => {
    if (!value) return;
    if (typeof value === 'string') {
      names.push(value);
      return;
    }
    if (typeof value === 'object' && value.name) {
      names.push(String(value.name));
    }
  };

  if (!profile) return names;
  if (Array.isArray(profile.permissions)) {
    profile.permissions.forEach(add);
  }
  const roles = Array.isArray(profile.roles) ? profile.roles : [];
  roles.forEach((role) => {
    if (Array.isArray(role?.permission_names)) role.permission_names.forEach(add);
    if (Array.isArray(role?.permissions)) role.permissions.forEach(add);
  });
  return names;
}

/**
 * Builds a permission map from profile response
 * @param {Object} profile - Profile response from /v2/users/profile
 * @returns {Record<string, boolean>} Permission map
 */
export function buildPermissionMap(profile) {
  const map = {};
  const names = collectPermissionNames(profile);
  if (!names.length) {
    return map;
  }

  names.forEach((name) => {
    map[name] = true;
  });

  // Platform operator supersedes admin.* (CLI-managed developer role)
  if (map['platform.*'] || map['platform.operator']) {
    map['platform.*'] = true;
    map['*'] = true; // everything, including platform-only gates
  }

  // admin.* is NOT expanded to '*' — it must not grant platform.* checks.
  // hasPermission / canAccess apply admin.* as "all non-platform".

  return map;
}

/** True when the permission name is CLI-only platform operator. */
export function isPlatformPermission(name) {
  if (!name || typeof name !== 'string') return false;
  const n = name.toLowerCase();
  return n === 'platform.*' || n === 'platform.operator' || n.startsWith('platform.');
}

/** True when the role slug is the CLI-only platform operator role. */
export function isPlatformRole(name) {
  return typeof name === 'string' && name.toLowerCase() === 'platform_operator';
}

/**
 * True when the signed-in care-admin user is a platform operator.
 * @param {object|null|undefined} user
 */
export function isPlatformOperatorUser(user) {
  if (!user) return false;
  if (user.is_platform_operator || user.isPlatformOperator) return true;
  if (user?.profile?.is_platform_operator) return true;
  const perms = user?.profile?.permissions;
  const map = Array.isArray(perms)
    ? buildPermissionMap({ permissions: perms })
    : (perms && typeof perms === 'object' ? { ...perms } : {});
  if (map['platform.*'] || map['platform.operator']) return true;
  if (Array.isArray(perms) && perms.some((p) => isPlatformPermission(p))) return true;
  const roles = user?.profile?.roles || user?.roles || [];
  if (Array.isArray(roles) && roles.some((r) => isPlatformRole(r?.name || r?.role_name || r))) {
    return true;
  }
  return false;
}

/**
 * Resolve whether a single required permission is held, applying wildcards.
 */
export function hasPermission(permission, permissionMap) {
  if (!permissionMap || !permission) return false;

  // platform.* / * → everything
  if (permissionMap['*'] || permissionMap['platform.*'] || permissionMap['platform.operator']) {
    return true;
  }

  if (permissionMap[permission]) return true;

  if (permissionAliasFamily(permission).some((alias) => permissionMap[alias])) {
    return true;
  }

  // admin.* → all non-platform
  if (permissionMap['admin.*'] && !isPlatformPermission(permission)) {
    return true;
  }

  // Other resource.* wildcards held by the user (e.g. email_campaigns.*)
  const held = Object.keys(permissionMap).filter((k) => permissionMap[k] && k.includes('*'));
  for (const wild of held) {
    if (wild === 'admin.*' || wild === 'platform.*') continue;
    // Mirrors the API's str_replace('\*', '.*', preg_quote($name, '/')): every regex
    // metacharacter — '*' INCLUDED — is escaped first, then the escaped '\*' becomes '.*'.
    // Leaving '*' out of the class makes the second replace a no-op, so 'claims.*' would
    // compile to /^claims\.*$/ ("claims" + any run of literal dots) and never match
    // 'claims.read'. It must compile to /^claims\..*$/.
    const escaped = wild.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*');
    if (new RegExp(`^${escaped}$`).test(permission)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if user has access to a module.
 * Resolves anyOf from the live catalog, then the built-in row for that key —
 * so canAccess('preauthApproval') is true when the user holds preauth.decide
 * / preauth.approve even if the DB catalog has not seeded that module yet.
 * @param {string} moduleKey - Module key from the access catalog
 * @param {Record<string, boolean>} permissionMap - Permission map
 * @returns {boolean} Whether user has access
 */
export function canAccess(moduleKey, permissionMap) {
  if (!permissionMap || !moduleKey || Object.keys(permissionMap).length === 0) {
    return false;
  }

  const required = getModuleAnyOf(moduleKey);
  if (!required.length) return false;

  return required.some((permission) => hasPermission(permission, permissionMap));
}

/**
 * Checks if user has any of the specified permissions
 * @param {string[]} permissions - Array of permission names
 * @param {Record<string, boolean>} permissionMap - Permission map
 * @returns {boolean} Whether user has at least one permission
 */
export function hasAnyPermission(permissions, permissionMap) {
  if (!permissionMap || !permissions || !Array.isArray(permissions)) return false;
  return permissions.some((permission) => hasPermission(permission, permissionMap));
}

/**
 * Checks if user has all of the specified permissions
 * @param {string[]} permissions - Array of permission names
 * @param {Record<string, boolean>} permissionMap - Permission map
 * @returns {boolean} Whether user has all permissions
 */
export function hasAllPermissions(permissions, permissionMap) {
  if (!permissionMap || !permissions || !Array.isArray(permissions)) return false;
  return permissions.every((permission) => hasPermission(permission, permissionMap));
}

/** Modules the given permission map would unlock in Care Admin. */
export function modulesUnlockedByMap(permissionMap) {
  if (!permissionMap || Object.keys(permissionMap).length === 0) return [];
  return getAccessModules().filter((mod) => canAccess(mod.key, permissionMap));
}

/** True when the session has at least one RBAC permission key. */
export function hasPopulatedPermissionMap(permissionMap, user) {
  if (permissionMap && Object.keys(permissionMap).length > 0) return true;
  const raw = user?.profile?.permissions;
  if (Array.isArray(raw)) return raw.length > 0;
  if (raw && typeof raw === 'object') return Object.keys(raw).length > 0;
  return false;
}

/**
 * Permission-first page stay. Legacy staffRoleId applies only when the map is empty.
 * Never use this to log the user out — send them to another allowed screen.
 */
export function canStayOnModule(moduleKey, permissionMap, user, legacyRoleIds = []) {
  if (canAccess(moduleKey, permissionMap)) return true;
  if (hasPopulatedPermissionMap(permissionMap, user)) return false;
  const roleId = Number(user?.role?.staffRoleId);
  return Boolean(roleId && legacyRoleIds.includes(roleId));
}

/**
 * Permission-first action gate. Legacy staffRoleId applies only when the map is empty.
 *
 * Same contract as canStayOnModule, for the call sites that check a permission list
 * rather than a catalog module. Use this — never an inline staffRoleId literal — when
 * migrating a legacy role gate, so a populated RBAC map can never be overridden by the
 * legacy role id.
 *
 * @param {string[]} permissionList - Permissions that grant the action (any one is enough)
 * @param {Record<string, boolean>} permissionMap - Permission map
 * @param {object} user - Session user (for the legacy staffRoleId + profile fallback)
 * @param {number[]} legacyRoleIds - Legacy staff role ids allowed while the map is empty
 * @returns {boolean}
 */
export function hasAnyPermissionWithLegacy(permissionList, permissionMap, user, legacyRoleIds = []) {
  if (hasAnyPermission(permissionList, permissionMap)) return true;
  if (hasPopulatedPermissionMap(permissionMap, user)) return false;
  const roleId = Number(user?.role?.staffRoleId);
  return Boolean(roleId && legacyRoleIds.includes(roleId));
}

/** Roles attached to a session profile. */
export function rolesFromUser(user) {
  if (Array.isArray(user?.profile?.roles)) return user.profile.roles;
  if (Array.isArray(user?.roles)) return user.roles;
  return [];
}

/** First catalog home route the user may open. Role home_module wins, then HOME_MODULE_ORDER. */
export function firstAllowedHome(permissionMap, roles = []) {
  if (!permissionMap || Object.keys(permissionMap).length === 0) {
    return null;
  }

  const fromRoles = (Array.isArray(roles) ? roles : [])
    .map((role) => ({
      module: role?.home_module || role?.homeModule || '',
      priority: Number(role?.home_priority ?? role?.homePriority ?? 0),
    }))
    .filter((row) => row.module && canAccess(row.module, permissionMap));

  fromRoles.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    const ia = HOME_MODULE_ORDER.indexOf(a.module);
    const ib = HOME_MODULE_ORDER.indexOf(b.module);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  if (fromRoles.length) {
    const mod = getAccessModule(fromRoles[0].module);
    return {
      path: mod?.routes?.[0] || '/dashboard',
      label: mod?.label || 'Home',
    };
  }

  for (const key of HOME_MODULE_ORDER) {
    if (canAccess(key, permissionMap)) {
      const mod = getAccessModule(key);
      return {
        path: mod?.routes?.[0] || '/dashboard',
        label: mod?.label || 'Dashboard',
      };
    }
  }
  return { path: '/dashboard', label: 'Dashboard (Overview)' };
}
